import { Link } from '@tanstack/react-router'
import { Info } from 'lucide-react'
import { Button } from '@deepguide-ai/ui/button'

export function hasVaultDocumentVersionChanges(submittedRevision: number | null, currentRevision?: number | null, currentHasChanges = false) {
  return submittedRevision !== null && (currentHasChanges || (currentRevision != null && currentRevision > submittedRevision))
}

/** Informational only: a newer working document never replaces submitted evidence. */
export function VaultDocumentVersionNotice({ submittedVersionId, submittedRevision, currentRevision, currentHasChanges = false,
  workspaceId, documentId, linkToCurrent = true, statusId, detailed = false,
}: {
  submittedVersionId: string | null
  submittedRevision: number | null
  currentRevision?: number | null
  currentHasChanges?: boolean
  workspaceId: string
  documentId: string
  linkToCurrent?: boolean
  statusId?: string
  detailed?: boolean
}) {
  if (!submittedVersionId || !hasVaultDocumentVersionChanges(submittedRevision, currentRevision, currentHasChanges)) return null
  return <div className='space-y-2 text-xs'>
    <div className='text-status-orange-strong flex items-start gap-1.5 font-medium'>
      <Info className='mt-0.5 size-3 shrink-0' aria-hidden='true' />
      <span>{currentHasChanges ? 'Document has changed' : 'Newer version available'}</span>
    </div>
    <dl className='grid grid-cols-2 gap-3'>
      <div className='space-y-1'>
        <dt className='text-muted-foreground'>Submitted version</dt>
        <dd><Link className='text-foreground font-medium hover:underline' aria-label={`View submitted version v${submittedRevision}`}
        to='/workspaces/$workspaceId/documents/$docId' params={{ workspaceId, docId: documentId }}
        search={{ versionId: submittedVersionId }}>v{submittedRevision}</Link></dd>
      </div>
      <div className='space-y-1'>
        <dt className='text-muted-foreground'>Current version</dt>
        <dd className='text-foreground font-medium'>
        {linkToCurrent ? <Link className='hover:underline' aria-label='View current document'
          to='/workspaces/$workspaceId/documents/$docId' params={{ workspaceId, docId: documentId }}
          search={{ versionId: undefined }}>{currentHasChanges ? 'Unversioned changes' : `v${currentRevision}`}</Link>
          : <span>{currentHasChanges ? 'Unversioned changes' : `v${currentRevision}`}</span>}
        </dd>
      </div>
    </dl>
    <div className='flex flex-wrap items-center justify-between gap-2 border-t border-status-orange/20 pt-2'>
      <p className='text-muted-foreground'>{detailed
      ? `This submission still uses v${submittedRevision}. To submit current content, first return this Test to an editable state using its existing workflow. Previous submissions stay in history.`
      : 'Evidence stays frozen.'}</p>
      {statusId && <Button variant='ghost' size='sm' className='h-7 px-2 text-xs text-foreground' asChild><Link
      to='/workspaces/$workspaceId/controls' params={{ workspaceId }} search={{ test: statusId }}>Open test →</Link></Button>}
    </div>
  </div>
}
